/**
 * Cities API Types & Schemas
 * Data Transfer Objects (DTOs) for API communication with Strapi
 */

import { z } from '@spcc/core'

// ===========================================
// RELATED ENTITY SCHEMAS
// ===========================================

/** Region DTO schema */
export const regionDtoSchema = z.object({
  id: z.union([z.string(), z.number()]),
  documentId: z.string(),
  code: z.string(),
  name: z.string(),
  isActive: z.boolean().optional().default(true),
})

/** Country DTO schema */
export const countryDtoSchema = z.object({
  id: z.union([z.string(), z.number()]),
  documentId: z.string(),
  code: z.string(),
  name: z.string(),
  isActive: z.boolean().optional().default(true),
})

// ===========================================
// CITY API SCHEMAS
// ===========================================

/**
 * City DTO Schema - What Strapi API returns
 * Maps to api::city.city schema
 */
export const cityDtoSchema = z.object({
  id: z.union([z.string(), z.number()]),
  documentId: z.string(),
  code: z.string(),
  name: z.string(),
  stateProvince: z.string().nullable().optional(),
  salesDistrict: z.string().nullable().optional(),
  isActive: z.boolean().default(true),
  locale: z.string().optional(),
  localizations: z
    .array(
      z.object({
        locale: z.string(),
        name: z.string().optional(),
        stateProvince: z.string().nullable().optional(),
      })
    )
    .optional(),
  // Relations (optional, populated based on query)
  region: regionDtoSchema.nullable().optional(),
  country: countryDtoSchema.nullable().optional(),
  // Timestamps
  createdAt: z.string(),
  updatedAt: z.string(),
})

/**
 * Create City Request Schema
 */
export const createCityRequestSchema = z.object({
  code: z.string().min(1, 'Code is required').max(50),
  name: z.string().min(1, 'Name is required').max(100),
  stateProvince: z.string().optional(),
  salesDistrict: z.string().max(50).optional(),
  isActive: z.boolean().optional().default(true),
  region: z.union([z.string(), z.number()]).optional(), // Region ID (optional)
  country: z.union([z.string(), z.number()]).optional(), // Country ID
})

/**
 * Update City Request Schema
 */
export const updateCityRequestSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  stateProvince: z.string().nullable().optional(),
  salesDistrict: z.string().max(50).nullable().optional(),
  isActive: z.boolean().optional(),
  region: z.union([z.string(), z.number()]).optional(),
  country: z.union([z.string(), z.number()]).nullable().optional(),
})

/**
 * City List Query Schema - Filtering/pagination for Strapi
 */
export const cityListQuerySchema = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(500).default(20),
  search: z.string().optional(),
  isActive: z.boolean().optional(),
  regionId: z.string().optional(),
  sort: z.string().optional().default('name:asc'),
})

/**
 * Strapi Pagination Meta Schema
 */
export const strapiPaginationSchema = z.object({
  page: z.number(),
  pageSize: z.number(),
  pageCount: z.number(),
  total: z.number(),
})

/**
 * City List Response Schema - Strapi paginated response
 */
export const cityListResponseSchema = z.object({
  data: z.array(cityDtoSchema),
  meta: z.object({
    pagination: strapiPaginationSchema,
  }),
})

/**
 * Single City Response Schema - Strapi single response
 */
export const cityResponseSchema = z.object({
  data: cityDtoSchema,
  meta: z.object({}).optional(),
})

// ===========================================
// INFERRED TYPES (from schemas)
// ===========================================

/** Region DTO type */
export type RegionDto = z.infer<typeof regionDtoSchema>

/** Country DTO type */
export type CountryDto = z.infer<typeof countryDtoSchema>

/** City DTO type */
export type CityDto = z.infer<typeof cityDtoSchema>

/** Create City request type */
export type CreateCityApiRequest = z.infer<typeof createCityRequestSchema>

/** Update City request type */
export type UpdateCityApiRequest = z.infer<typeof updateCityRequestSchema>

/** City list query params type */
export type CityListQuery = z.infer<typeof cityListQuerySchema>

/** City list response type */
export type CityListResponse = z.infer<typeof cityListResponseSchema>

/** Single City response type */
export type CityResponse = z.infer<typeof cityResponseSchema>

/** Strapi pagination type */
export type StrapiPagination = z.infer<typeof strapiPaginationSchema>

// ===========================================
// VALIDATION HELPERS
// ===========================================

/**
 * Validate city DTO from API response
 * @throws ZodError if validation fails
 */
export function validateCityDto(data: unknown): CityDto {
  return cityDtoSchema.parse(data)
}

/**
 * Validate city list response from API
 * @throws ZodError if validation fails
 */
export function validateCityListResponse(data: unknown): CityListResponse {
  return cityListResponseSchema.parse(data)
}

/**
 * Safe parse - returns success/error instead of throwing
 */
export function safeParseCityDto(data: unknown) {
  return cityDtoSchema.safeParse(data)
}

export function safeParseCityList(data: unknown) {
  return cityListResponseSchema.safeParse(data)
}

// ===========================================
// API ENDPOINTS (constants)
// ===========================================

export const CITY_ENDPOINTS = {
  LIST: '/api/cities',
  DETAIL: (documentId: string) => `/api/cities/${documentId}`,
  CREATE: '/api/cities',
  UPDATE: (documentId: string) => `/api/cities/${documentId}`,
  DELETE: (documentId: string) => `/api/cities/${documentId}`,
} as const

// ===========================================
// POPULATE PARAMS (for Strapi)
// ===========================================

export const CITY_POPULATE = {
  DEFAULT: {
    // locale is injected once per GET by HttpClient (see setLocaleProvider); do not set here
    'populate[localizations][fields][0]': 'locale',
    'populate[localizations][fields][1]': 'name',
    'populate[localizations][fields][2]': 'stateProvince',
    'populate[region][fields][0]': 'documentId',
    'populate[region][fields][1]': 'name',
    'populate[region][fields][2]': 'code',
    'populate[country][fields][0]': 'documentId',
    'populate[country][fields][1]': 'name',
    'populate[country][fields][2]': 'code',
  },
  MINIMAL: {},
} as const
